import lodashCompact from 'lodash-es/compact';
import lodashKebabCase from 'lodash-es/kebabCase';
import lodashSnakeCase from 'lodash-es/snakeCase';

import { SellingMethod } from '~/apiClient';
import { EMPTY_OPTION_VALUE } from '~/constants/filters';
import { ROUTE_NAMES, type RouteName } from '~/constants/routes';
import { Locale } from '~i18n/i18nConfig';

export type ListNameSource = 'page' | 'related';

export const eventTypeStandard = { event_type: 'ga4_ce' } as const;

export const pageTypeMap: Record<RouteName, string> = {
    [ROUTE_NAMES.HOMEPAGE]: 'homepage',
    [ROUTE_NAMES.AUCTION_LISTING]: 'alp',
    [ROUTE_NAMES.AUCTION_DETAIL]: 'adp',
    [ROUTE_NAMES.AUCTION_DETAIL_NEGOTIATION]: 'adp_negotiation',
    [ROUTE_NAMES.DIRECT_SALE_LISTING]: 'dslp',
    [ROUTE_NAMES.DIRECT_SALE_DETAIL]: 'dsdp',
    [ROUTE_NAMES.DIRECT_SALE_DETAIL_NEGOTIATION]: 'dsdp_negotiation',
    [ROUTE_NAMES.LOGIN]: 'login',
    [ROUTE_NAMES.SIGNUP]: 'sign_up',
    [ROUTE_NAMES.SIGNUP_ONBOARDING]: 'sign_up_onboarding',
    [ROUTE_NAMES.SIGNUP_CONFIRM]: 'sign_up_confirm',
    [ROUTE_NAMES.PASSWORD_RESET]: 'password_reset',
    [ROUTE_NAMES.COOKIE_POLICY]: 'cookie_policy',
    [ROUTE_NAMES.PRIVACY_POLICY]: 'privacy_policy',
    [ROUTE_NAMES.TERMS_AND_CONDITIONS]: 'terms_and_conditions',
    [ROUTE_NAMES.LOGISTICS_TERMS_AND_CONDITIONS]: 'logistics_terms_and_conditions',
    [ROUTE_NAMES.FAQ]: 'faq',
    [ROUTE_NAMES.PRICING_BUYER]: 'pricing_buyer',
    [ROUTE_NAMES.PRICING_SELLER]: 'pricing_seller',
    [ROUTE_NAMES.ACCESSIBILITY_STATEMENT]: 'accessibility_statement',
    [ROUTE_NAMES.SELL]: 'sell',
    [ROUTE_NAMES.HOW_TO_HOW_SELL_USED_BUS]: 'how_sell_used_bus',
    [ROUTE_NAMES.HOW_TO_REASONS_BUY_BUS_AUCTION]: 'reasons_buy_bus_auction',
    [ROUTE_NAMES.HOW_TO_GUIDE_BUY_USED_BUS]: 'guide_buy_used_bus',
    [ROUTE_NAMES.HOW_TO_TRUST_REPORT]: 'trust_report',
    [ROUTE_NAMES.ACCOUNT_BIDS]: 'account_bids',
    [ROUTE_NAMES.ACCOUNT_PROFILE]: 'account_profile',
    [ROUTE_NAMES.ACCOUNT_YOUR_AUCTIONS]: 'account_yours_auctions',
    [ROUTE_NAMES.ACCOUNT_YOUR_DIRECT_SALE_BUYER_NEGOTIATIONS]: 'account_yours_direct_sale_buyer_negotiations',
    [ROUTE_NAMES.ACCOUNT_YOUR_DIRECT_SALES]: 'account_yours_direct_sales',
    [ROUTE_NAMES.ACCOUNT_ORDERS_AUCTION]: 'account_orders_auction',
    [ROUTE_NAMES.ACCOUNT_ORDERS]: 'account_orders_direct_sales',
    [ROUTE_NAMES.ACCOUNT_ORDER]: 'account_orders_direct_sales_detail',
    [ROUTE_NAMES.ACCOUNT_ORDER_THANK_YOU]: 'dsdp_thank_you',
    [ROUTE_NAMES.ACCOUNT_ORDER_EDIT]: 'account_orders_direct_sales_detail_edit',
    [ROUTE_NAMES.ACCOUNT_ORDER_EDIT_PAYMENT]: 'account_orders_direct_sales_detail_edit_payment',
    [ROUTE_NAMES.ACCOUNT_PAYMENT_METHODS]: 'account_payment_methods',
    [ROUTE_NAMES.ACCOUNT_FAVORITES]: 'account_favorites',
    [ROUTE_NAMES.SMART_LINK_VEHICLE]: 'vehicle_smart_link',
    [ROUTE_NAMES.BLACK_WEEK]: 'black_week',
    [ROUTE_NAMES.IMPRESSUM]: 'impressum',
    [ROUTE_NAMES.STORYBLOK_PREVIEW]: 'storyblok_preview',
} as const;

export const getPageType = (): string => {
    const route = useRoute();
    const routeBaseName = useRouteBaseName();
    const routeName = routeBaseName(route);

    return isRouteName(routeName) ? pageTypeMap[routeName] : lodashSnakeCase(routeName?.toString() ?? 'unknown');
};

export const push = (data: Record<string, any>, flush: boolean = true): void => {
    const config = useRuntimeConfig();

    if (config.public.gtmDebugDev && import.meta.dev) {
        // eslint-disable-next-line no-console
        console.log('GTM push:', data);
    }

    if (config.public.googleTagManager.disabled) {
        return;
    }

    const { proxy } = useScriptGoogleTagManager({ scriptInput: { src: config.public.googleTagManager.src, async: true, defer: true } });

    if (!proxy?.dataLayer) return;

    proxy.dataLayer.push(data);

    if (flush) {
        const resetData = Object.keys(data).reduce((obj, key) => ({ ...obj, [key]: null }), {});
        proxy.dataLayer.push(resetData);
    }
};

export const wrapTrackFunctions = <T extends Record<string, Function>>(obj: T): T => {
    const { logError } = useLogs();
    const wrappedFunctions = {} as T;

    for (const [key, fn] of Object.entries(obj)) {
        wrappedFunctions[key as keyof T] = ((...args: any[]): void => {
            try {
                return (fn as Function)(...args);
            } catch (error) {
                logError('Error in GTM track', { trackFn: key, error });
            }
        }) as any;
    }

    return wrappedFunctions;
};

export const formatNumber = (n: number): string => n.toLocaleString('en-US');

export const formatMileage = (km: number): string => {
    const thresholds = [50_000, 100_000, 200_000, 500_000, 1_000_000];

    if (km < thresholds[0]) {
        return `less than ${formatNumber(thresholds[0])} km`;
    }

    for (let i = 0; i < thresholds.length - 1; i++) {
        const lower = thresholds[i];
        const upper = thresholds[i + 1];

        if (km >= lower && km < upper) {
            return `from ${formatNumber(lower)} to ${formatNumber(upper)} km`;
        }
    }

    return `over ${formatNumber(thresholds[thresholds.length - 1])} km`;
};

export const formatTimeRemaining = (date: string): string => {
    const { timeSyncNow } = useTimeSync();
    const dayjs = useDayjs();

    const remainingSeconds = Math.max(Math.round((Date.parse(date) - timeSyncNow.value) / 1000), 0);

    if (remainingSeconds === 0) {
        return 'zero';
    }

    const thresholds = [
        dayjs.duration({ minutes: 30 }),
        dayjs.duration({ hours: 1 }),
        dayjs.duration({ hours: 3 }),
        dayjs.duration({ days: 1 }),
        dayjs.duration({ days: 3 }),
    ];

    const secondsThresholds = thresholds.map(d => d.asSeconds());

    if (remainingSeconds < secondsThresholds[0]) {
        return `less than ${thresholds[0].humanize()}`;
    }

    for (let i = 0; i < secondsThresholds.length - 1; i++) {
        const lower = secondsThresholds[i];
        const upper = secondsThresholds[i + 1];

        if (remainingSeconds >= lower && remainingSeconds < upper) {
            return `from ${thresholds[i].humanize()} to ${thresholds[i + 1].humanize()}`;
        }
    }

    return `over ${thresholds[thresholds.length - 1].humanize()}`;
};

export const formatTrackingListName = (source: ListNameSource): string | null => {
    if (source === 'related') return 'related_vehicles';

    const route = useRoute();
    const routeBaseName = useRouteBaseName();
    const routeName = routeBaseName(route);

    if (routeName === ROUTE_NAMES.ACCOUNT_BIDS) return 'your_bids';
    if (routeName === ROUTE_NAMES.ACCOUNT_YOUR_AUCTIONS) return 'your_auctions';
    if (routeName === ROUTE_NAMES.ACCOUNT_YOUR_DIRECT_SALES) return 'your_buy_now';
    if (routeName === ROUTE_NAMES.ACCOUNT_FAVORITES) return 'favorites';

    if (isSlpRoute(routeName)) {
        const sellingMethod = getSellingMethodFromRouteName(routeName);
        const sellingMethodListNamePart = { [SellingMethod.Auction]: 'auctions', [SellingMethod.DirectSale]: 'direct-sales' }[sellingMethod];
        const slpParsedRouteParamsState = useSlpParsedRouteParamsState(route);
        const { assetType, platformClass, brandUrlKey, modelUrlKey } = slpParsedRouteParamsState.value;
        const localizedEnClass = platformClass && localizedParams[Locale.EN].class[assetType][platformClass];

        return lodashCompact([sellingMethodListNamePart, assetType, localizedEnClass, brandUrlKey, modelUrlKey]).map(lodashKebabCase).join('_');
    }

    return null;
};

export interface VideoTrackerOptions {
    thresholds?: number[]; // Thresholds in percentage (e.g. [25, 50, 75])
    onPlay: () => void;
    onProgress: (percent: number) => void;
    onEnded: () => void;
}

export const attachVideoTracker = (video: HTMLVideoElement, { thresholds = [10, 25, 50, 75], onPlay, onProgress, onEnded }: VideoTrackerOptions) => {
    const firedThresholds = new Set<number>();

    function handleTimeUpdate(): void {
        if (!video.duration || isNaN(video.duration)) return;
        const percent = (video.currentTime / video.duration) * 100;

        thresholds.forEach(threshold => {
            if (percent >= threshold && !firedThresholds.has(threshold)) {
                firedThresholds.add(threshold);
                onProgress(threshold);
            }
        });
    }

    video.addEventListener('play', onPlay);
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('ended', onEnded);

    return function cleanupFn(): void {
        video.removeEventListener('play', onPlay);
        video.removeEventListener('timeupdate', handleTimeUpdate);
        video.removeEventListener('ended', onEnded);
    };
};

export const formatSortType = (sortSelectModel: string): string => {
    const safeJsonParse = useSafeJsonParse();

    if (sortSelectModel === EMPTY_OPTION_VALUE) return 'default_order';

    const parsedValue = safeJsonParse<SortingParams<string>>(sortSelectModel, `tracking formatSortType: ${sortSelectModel}`);
    if (!parsedValue) return 'unknown';
    const { sortBy, sortOrder } = parsedValue;

    return lodashSnakeCase(`${sortBy}_${sortOrder}`);
};
